/**
 * Stops the mouse wheel / trackpad from changing the value of a focused
 * `<input type="number">`.
 *
 * Every payment, adjustment, and fee field in the dashboard is a number input
 * with a one-cent step. Browsers treat a wheel event over a *focused* number
 * input as an increment/decrement, and a Mac trackpad fires wheel events at the
 * slightest two-finger touch, so simply moving the pointer across a field the
 * manager had just clicked into would roll the amount up or down a cent at a
 * time — silently, after the amount had already been reviewed.
 *
 * The guard runs on the capture phase so it lands before any React handler, and
 * it only fires when the field under the pointer is the focused one, which is
 * the only case where the browser would have changed the value. Anywhere else —
 * an unfocused field, the rest of the page — scrolling behaves normally.
 */

const isFocusedNumberInput = (target: EventTarget | null): boolean =>
  target instanceof HTMLInputElement &&
  target.type === 'number' &&
  target === document.activeElement;

const handleWheel = (event: WheelEvent): void => {
  if (isFocusedNumberInput(event.target)) {
    // Keeps the caret and the typed value intact; only the increment is lost.
    event.preventDefault();
  }
};

/** Installs the guard for the life of the page. */
export function installNumberInputScrollGuard(): void {
  // passive: false is required for preventDefault() to take effect on wheel.
  document.addEventListener('wheel', handleWheel, { passive: false, capture: true });
}
